import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Users, Calendar, CheckSquare, Settings, ArrowRight, Check, Rocket, X } from 'lucide-react';
import AIUsageWidget from '../components/dashboard/AIUsageWidget';
import api from '../api/client';

export default function Dashboard() {
  const { user, tenant } = useAuthStore();
  const navigate = useNavigate();
  const [planInfo, setPlanInfo] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    api.get('/services/plan').then(r => setPlanInfo(r.data?.plan)).catch(() => {});
  }, []);

  const isFreePlan = planInfo && planInfo.name === 'free';

  return (
    <div className="max-w-5xl mx-auto">
      {/* Free plan upgrade banner */}
      {isFreePlan && !bannerDismissed && (
        <div className="mb-6 bg-gradient-to-r from-brand-50 to-blue-50 border border-brand-200 rounded-xl px-5 py-4 flex items-center gap-4">
          <Rocket className="h-6 w-6 text-brand-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              You're on the Free plan ({planInfo.max_users || 1} user, {planInfo.max_records || 10} records, {planInfo.included_services?.ai_rag?.monthly_limit || 20} AI queries)
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Upgrade to unlock document generation, more users, and more.</p>
          </div>
          <a
            href="https://sixty10.com/pricing/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            View Plans
          </a>
          <button onClick={() => setBannerDismissed(true)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.first_name}
        </h1>
        <p className="text-gray-500 mt-1">{tenant?.name}</p>
      </div>

      {/* Quick stats - placeholder */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Active Cases', value: '—', icon: Briefcase, color: 'text-blue-600 bg-blue-50' },
          { label: 'Contacts', value: '—', icon: Users, color: 'text-green-600 bg-green-50' },
          { label: 'Events This Week', value: '—', icon: Calendar, color: 'text-purple-600 bg-purple-50' },
          { label: 'Pending Tasks', value: '—', icon: CheckSquare, color: 'text-orange-600 bg-orange-50' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                <stat.icon className="h-5 w-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* AI Usage Widget */}
      <div className="mb-8">
        <AIUsageWidget />
      </div>

      {/* Getting started checklist */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Getting Started</h2>

        {/* Step progress bar */}
        {(() => {
          const steps = [
            { label: 'Create Record Type', description: 'Set up your first record type to organize your cases', done: false, path: '/admin/record-types' },
            { label: 'Custom Fields', description: 'Add custom fields to capture the data you need', done: false, path: '/admin/record-types' },
            { label: 'Screens', description: 'Design screen layouts for your record types', done: false, path: '/admin/record-types' },
            { label: 'Stages & Workflows', description: 'Set up stages and workflows to track case progress', done: false, path: '/admin/record-types' },
            { label: 'Invite Team', description: 'Invite your team members to start collaborating', done: false, path: '/admin/users' },
          ];
          const currentStep = steps.findIndex(s => !s.done);
          const activeIndex = currentStep === -1 ? steps.length - 1 : currentStep;

          return (
            <>
              {/* Horizontal step indicators */}
              <div className="flex items-center mb-6 px-2">
                {steps.map((step, i) => (
                  <React.Fragment key={i}>
                    <button
                      onClick={() => navigate(step.path)}
                      className="flex flex-col items-center group flex-shrink-0"
                      title={step.label}
                    >
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors cursor-pointer ${
                        step.done
                          ? 'bg-green-500 text-white'
                          : i === activeIndex
                            ? 'bg-brand-600 text-white ring-4 ring-brand-100'
                            : 'bg-gray-100 text-gray-400 border-2 border-gray-300 group-hover:border-brand-400 group-hover:text-brand-500'
                      }`}>
                        {step.done ? <Check className="h-4 w-4" /> : i + 1}
                      </div>
                      <span className={`text-xs mt-1.5 text-center max-w-[80px] leading-tight ${
                        step.done
                          ? 'text-green-600 font-medium'
                          : i === activeIndex
                            ? 'text-brand-600 font-medium'
                            : 'text-gray-400'
                      }`}>
                        {step.label}
                      </span>
                    </button>
                    {i < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-1 mt-[-18px] ${
                        step.done ? 'bg-green-400' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Step details list */}
              <div className="space-y-2">
                {steps.map((step, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(step.path)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      i === activeIndex ? 'bg-brand-50 border border-brand-200' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      step.done
                        ? 'bg-green-500 text-white'
                        : i === activeIndex
                          ? 'bg-brand-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                    }`}>
                      {step.done ? <Check className="h-3 w-3" /> : i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium ${
                        step.done ? 'text-gray-400 line-through' : 'text-gray-700'
                      }`}>
                        {step.label}
                      </span>
                      <p className="text-xs text-gray-400 mt-0.5">{step.description}</p>
                    </div>
                    <ArrowRight className={`h-4 w-4 flex-shrink-0 ${
                      i === activeIndex ? 'text-brand-500' : 'text-gray-300'
                    }`} />
                  </button>
                ))}
              </div>
            </>
          );
        })()}

        {user?.is_admin && (
          <button
            onClick={() => navigate('/admin')}
            className="mt-4 flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            <Settings className="h-4 w-4" />
            Go to Admin Panel
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
